// src/pages/Home.jsx
import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";

/* ---------- Thin section divider ---------- */
function SectionDivider({ className = "" }) {
  return (
    <div className={`relative left-1/2 -translate-x-1/2 w-screen ${className}`}>
      <div className="mx-auto max-w-6xl border-t border-slate-200" />
    </div>
  );
}

/* ---------- Testimonial Carousel ---------- */
function TestimonialCarousel() {
  const slides = [
    {
      img: "/assets/images/testimonial4.png",
      quote:
        "Market Fusion made it incredibly easy to find a reliable manufacturer for our clean skincare line. We went from idea to production in under 3 months!",
      name: "Thomas Jefferson",
      role: "Member – Cultiva Group",
    },
    {
      img: "/assets/images/testimonial2.png",
      quote:
        "We shortlisted partners in days instead of weeks. The process and communication have been amazing.",
      name: "Ava Martinez",
      role: "Founder – Luma Beauty",
    },
    {
      img: "/assets/images/testimonial1.png",
      quote:
        "Transparent pricing and vetted factories helped us launch faster with confidence.",
      name: "Noah Chen",
      role: "COO – PureLab Skincare",
    },
  ];

  const [i, setI] = useState(0);
  const len = slides.length;
  const prev = () => setI((p) => (p - 1 + len) % len);
  const next = () => setI((p) => (p + 1) % len);
  const goTo = (k) => setI(k);

  const { img, quote, name, role } = slides[i];

  return (
    <section className="relative w-screen left-1/2 -translate-x-1/2 py-12 md:py-14" style={{ backgroundColor: "rgba(248, 246, 250, 100)" }}>
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        

        <div className="grid items-center gap-8 md:grid-cols-[1fr,1.6fr]">
          {}
          
          <div className="flex justify-center">
            
            <div className="relative h-64 w-64 md:h-80 md:w-80">
              <div className="absolute inset-0 rounded-full bg-white/70 shadow-inner" />
              <img
                src={img}
                alt={name}
                className="absolute left-1/2 top-1/2 h-[76%] w-[76%] -translate-x-1/2 -translate-y-1/2 rounded-full object-cover shadow"
              />
            </div>
          </div>

          <div>
            <h2 className="text-2xl md:text-3xl font-semibold mb-8">
  What our <span className="text-rose-600">clients</span> say
</h2>

<p className="text-base md:text-lg leading-relaxed text-slate-700 min-h-[60px]">
  {quote}
</p>

            <div className="mt-6">
              <p className="text-xl md:text-2xl font-semibold italic">{name}</p>
              <p className="text-sm md:text-base text-slate-500">{role}</p>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={prev}
                aria-label="Previous testimonial"
                className="grid h-10 w-10 place-items-center rounded-full border border-slate-300 bg-white text-slate-700 shadow hover:bg-slate-50"
              >
                ‹
              </button>
              <button
                onClick={next}
                aria-label="Next testimonial"
                className="grid h-10 w-10 place-items-center rounded-full border border-slate-300 bg-white text-slate-700 shadow hover:bg-slate-50"
              >
                ›
              </button>
=
              <div className="ml-2 flex items-center gap-2">
                {slides.map((_, k) => (
                  <button
                    key={k}
                    onClick={() => goTo(k)}
                    aria-label={`Go to testimonial ${k + 1}`}
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      i === k ? "bg-rose-600" : "bg-slate-300"
                    }`}
                  />
                ))}

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Products Rail---------- */
function ProductsRail() {
  const products = [
    { title: "Clothing", img: "/assets/images/Clothing.png" },
    { title: "Electronics", img: "/assets/images/Electronics.png" },
    { title: "Home & Decor", img: "/assets/images/HomeDecor.png" },
    { title: "Stationery", img: "/assets/images/Stationery.png" },
  ];

  const extended = [products[products.length - 1], ...products, products[0]];

  const railRef = useRef(null);
  const cardRefs = useRef([]);
  const [i, setI] = useState(1);
  const SMOOTH_MS = 380;

  useEffect(() => {
    cardRefs.current[1]?.scrollIntoView({
      behavior: "instant",
      inline: "start",
      block: "nearest",
    });
  }, []);

  const go = (dir) => {
    const next = i + dir;

    setI(next);
    cardRefs.current[next]?.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });

    window.clearTimeout(go._t);
    go._t = window.setTimeout(() => {
      if (next === 0) {
        setI(products.length);
        cardRefs.current[products.length]?.scrollIntoView({
          behavior: "instant",
          inline: "start",
          block: "nearest",
        });
      } else if (next === products.length + 1) {
        setI(1);
        cardRefs.current[1]?.scrollIntoView({
          behavior: "instant",
          inline: "start",
          block: "nearest",
        });
      }
    }, SMOOTH_MS);
  };

  return (
    <section className="mt-6">
      {/* hide native scrollbar */}
      <style>{`
        #productScroll::-webkit-scrollbar{ display:none; }
        #productScroll{ -ms-overflow-style:none; scrollbar-width:none; }
      `}</style>

      <div className="relative left-1/2 -translate-x-1/2 w-screen">
        <div
          id="productScroll"
          ref={railRef}
          className="mx-auto max-w-6xl flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 px-4 md:px-0"
        >
          {extended.map((item, idx) => (
            <article
              key={`${item.title}-${idx}`}
              ref={(el) => (cardRefs.current[idx] = el)}
              className="flex-none snap-start w-[320px] md:w-[360px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              aria-hidden={idx === 0 || idx === extended.length - 1 ? "true" : "false"}
            >
              <div className="w-full h-[240px] overflow-hidden">
                <img
                  src={item.img}
                  alt={item.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="px-4 py-3 text-center text-base font-medium text-slate-800">
                {item.title}

              </div>
            </article>
          ))}
        </div>

        {}
        <button
          type="button"
          onClick={() => go(-1)}
          className="absolute left-[calc(50%-40rem)] md:left-[calc(50%-36rem)] top-1/2 -translate-y-1/2 grid place-items-center h-10 w-10 rounded-full bg-white shadow border border-slate-200 hover:bg-slate-50"
          aria-label="Scroll left"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          className="absolute right-[calc(50%-40rem)] md:right-[calc(50%-36rem)] top-1/2 -translate-y-1/2 grid place-items-center h-10 w-10 rounded-full bg-white shadow border border-slate-200 hover:bg-slate-50"
          aria-label="Scroll right"
        >
          ›
        </button>
      </div>
    </section>
  );
}

/* ------------------------------- Page ------------------------------- */
export default function Home() {
  return (
    <main className="w-full text-slate-900">
      {/* ===== HERO ===== */}
      <section className="w-full">
        <div className="relative w-full overflow-hidden" style={{ height: 420 }}>
          <img
            src="/assets/images/MainBanner.png"
            alt="Banner"
            className="absolute inset-0 h-full w-full object-cover object-[50%_38%]"          />
          <div className="absolute inset-0 bg-gray-100/70" />
          <div className="relative z-10 flex h-full flex-col items-center justify-center text-center px-6">
            <h1 className="text-2xl md:text-3xl font-semibold leading-snug">
              Your trusted manufacturing partners <br />for every product category
            </h1>
            <Link
              to="/join"
              className="mt-6 inline-flex items-center rounded-md bg-rose-600 px-6 py-2.5 text-white shadow hover:bg-rose-700"
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>


      <SectionDivider className="my-6" />
      <ProductsRail />
      <SectionDivider className="my-6" />
      <TestimonialCarousel />
      <SectionDivider className="my-6" />

      {/* ===== CTA STRIP ===== */}
      <section className="mx-auto my-0 w-full max-w-6xl overflow-hidden rounded-2xl border border-slate-200 bg-rose-50">
  <div className="relative flex flex-col items-center justify-center text-center px-6 py-24 md:py-28">
    <img
      src="/assets/images/hackathon.png"
      alt="Product assortment"
      className="absolute inset-0 h-full w-full object-cover opacity-30"
    />
    <div className="absolute inset-0" style={{ backgroundColor: "rgba(248, 246, 250, 0.70)" }} />


    <div className="relative z-10">
      <h3 className="text-2xl md:text-3xl font-semibold mb-6">
        Let’s build your product brand!
      </h3>
      <div className="flex justify-center gap-4">
        <Link
          to="/join"
          className="rounded-md bg-rose-600 px-6 py-3 text-white font-medium hover:bg-rose-700"
        >
          Register
        </Link>
        <Link
          to="/signin"
          className="rounded-md border border-slate-300 px-6 py-3 font-medium hover:bg-white"
        >
          Sign In
        </Link>
      </div>
    </div>
  </div>
</section>


    </main>
  );
}
